import * as React from "react"
import { Box, Chip, IconButton, Tooltip, CircularProgress } from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import PauseIcon from "@mui/icons-material/Pause"
import PlayCircleIcon from "@mui/icons-material/PlayCircle"
import HistoryIcon from "@mui/icons-material/History"


const BATCH_TYPE_CHIPS = {
    sku2raw: { label: 'SKU to Product', color: 'primary' },
    raw2sku: { label: 'Product to SKU', color: 'secondary' },
    raw2raw: { label: 'Product to Product', color: 'info' },
    war2war: { label: 'Competitor to Own', color: 'warning' }
}


export const idScheduleColumn = () => ({
    name: 'idSchedule',
    label: 'ID',
    options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                #{value}
            </Box>
        )
    }
})

export const scheduleNameColumn = () => ({
    name: 'scheduleName',
    label: 'Schedule Name',
    options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
            <Box sx={{ fontWeight: 500 }}>
                {value}
            </Box>
        )
    }
})

export const batchTypeColumn = () => ({
    name: 'batchType',
    label: 'Batch Type',
    options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
            const config = BATCH_TYPE_CHIPS[value] || { label: value, color: 'default' }

            return <Chip label={config.label} color={config.color} size="small" />
        }
    }
})

export const frequencyColumn = () => ({
    name: 'frequencyLabel',
    label: 'Frequency',
    options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
            <Chip label={value} size="small" variant="outlined" />
        )
    }
})

export const isActiveColumn = () => ({
    name: 'isActive',
    label: 'Status',
    options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
            return value
                ? <Chip label="Active" color="success" size="small" />
                : <Chip label="Paused" color="default" size="small" />
        }
    }
})

// Office time, parsed field by field. `new Date(value)` would reinterpret a server-clock
// string as browser-local and shift the day for any hour near midnight — and now that a
// schedule can be pinned to 23:30, that is a real row and not a corner case.
const parseLocal = (value) => {
    if (!value) return null
    const [datePart, timePart = '00:00:00'] = String(value).split('T')
    const [y, m, d] = datePart.split('-').map(Number)
    const [hh, mm] = timePart.split(':').map(Number)
    if (!y || !m || !d) return null
    return new Date(y, m - 1, d, hh || 0, mm || 0)
}

const HHMM = { hour: '2-digit', minute: '2-digit' }

export const nextExecutionColumn = () => ({
    name: 'nextExecutionLocal',
    label: 'Next Execution',
    options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
            const date = parseLocal(value)
            if (!date) return <Box sx={{ color: 'text.disabled' }}>—</Box>

            const now = new Date()
            const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
            const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate())

            const isPast = dateOnly < todayOnly
            const isToday = dateOnly.getTime() === todayOnly.getTime()

            return (
                <Box sx={{
                    fontSize: '0.875rem',
                    fontWeight: isToday ? 600 : 400,
                    color: isPast ? 'error.main' : isToday ? 'warning.main' : 'text.primary'
                }}>
                    {date.toLocaleDateString()} {date.toLocaleTimeString([], HHMM)}
                </Box>
            )
        }
    }
})

export const lastExecutionColumn = () => ({
    name: 'lastExecutionLocal',
    label: 'Last Execution',
    options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
            const date = parseLocal(value)
            if (!date) return <Box sx={{ color: 'text.disabled' }}>Never</Box>

            return (
                <Box sx={{ fontSize: '0.875rem' }}>
                    {date.toLocaleDateString()} {date.toLocaleTimeString([], HHMM)}
                </Box>
            )
        }
    }
})

export const createdAtColumn = () => ({
    name: 'createdAt',
    label: 'Created',
    options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
            if (!value) return '—'
            const date = new Date(value)
            return date.toLocaleDateString()
        }
    }
})

export const actionsColumn = (handlers, loadingStates) => ({
    name: 'idSchedule',
    label: 'Actions',
    options: {
        filter: false,
        sort: false,
        customBodyRender: (id_schedule, tableMeta) => {
            const rowData = tableMeta.tableData[tableMeta.rowIndex]
            const isActive = rowData[5] // isActive column index

            return (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title={isActive ? "Pause" : "Activate"}>
                        <IconButton
                            size="small"
                            onClick={() => handlers.onToggleActive(id_schedule)}
                            disabled={loadingStates.toggling === id_schedule}
                        >
                            {loadingStates.toggling === id_schedule ? (
                                <CircularProgress size={20} />
                            ) : isActive ? (
                                <PauseIcon fontSize="small" />
                            ) : (
                                <PlayArrowIcon fontSize="small" />
                            )}
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Run Now">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handlers.onRunNow(id_schedule, rowData[1])}
                            disabled={loadingStates.running === id_schedule}
                        >
                            {loadingStates.running === id_schedule ? (
                                <CircularProgress size={20} />
                            ) : (
                                <PlayCircleIcon fontSize="small" />
                            )}
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="View History">
                        <IconButton
                            size="small"
                            onClick={() => handlers.onViewHistory(id_schedule, rowData[1])}
                        >
                            <HistoryIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Edit">
                        <IconButton
                            size="small"
                            onClick={() => handlers.onEdit(id_schedule)}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => handlers.onDelete(id_schedule, rowData[1])}
                            disabled={loadingStates.deleting === id_schedule}
                        >
                            {loadingStates.deleting === id_schedule ? (
                                <CircularProgress size={20} />
                            ) : (
                                <DeleteIcon fontSize="small" />
                            )}
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    }
})

// ── detection: is this schedule finding anything? ─────────────────────────────
// A schedule is its brands plus a cadence, so this is the unit the question belongs to.
// The per-site coverage panel that preceded these columns could not say WHICH schedule had
// stopped, which is the only thing an operator can act on.

export const producedColumn = () => ({
    name: 'tasks',
    label: 'Produced',
    options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta) => {
            const batches = tableMeta.rowData?.[tableMeta.columnIndex + 1]
            if (!value) {
                return <Tooltip title="This schedule has never produced a task">
                    <Box sx={{ color: 'text.disabled' }}>—</Box>
                </Tooltip>
            }
            return (
                <Tooltip title={`${batches || 0} batch(es) created in total`}>
                    <Box sx={{ fontSize: '0.875rem' }}>
                        {value.toLocaleString()} <Box component="span"
                            sx={{ color: 'text.secondary' }}>tasks</Box>
                    </Box>
                </Tooltip>
            )
        }
    }
})

export const batchesColumn = () => ({
    name: 'batches',
    label: 'Batches',
    options: { display: 'false', filter: false, sort: false }
})

export const unattemptedColumn = () => ({
    name: 'unattempted',
    label: 'Not detected',
    options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta) => {
            // null ≠ 0. null is "this direction cannot be answered scoped to the
            // schedule's brands" (raw2raw anchors on RETAILER_PRODUCT by brand NAME;
            // sku2raw has no date to select new SKUs by; a schedule with no brands covers
            // everything). Printing 0 there would read as "all covered".
            if (value === null || value === undefined) {
                return <Tooltip title="Not answerable for this batch type or scope">
                    <Box sx={{ color: 'text.disabled' }}>—</Box>
                </Tooltip>
            }
            const seen = tableMeta.rowData?.[tableMeta.columnIndex + 1]
            if (!value) {
                return <Tooltip title={`${seen || 0} new in the window, all attempted`}>
                    <Chip label="0" color="success" size="small" variant="outlined" />
                </Tooltip>
            }
            return (
                <Tooltip title={`${value.toLocaleString()} of ${(seen || 0).toLocaleString()} new products in this schedule's brands got no mapping attempt`}>
                    <Chip
                        label={value.toLocaleString()}
                        color="error"
                        size="small"
                        sx={{ fontWeight: 600 }}
                    />
                </Tooltip>
            )
        }
    }
})

export const seenColumn = () => ({
    name: 'seen',
    label: 'Seen',
    options: { display: 'false', filter: false, sort: false }
})
